import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import categorySEO from '../data/categorySEO';
import CategoryIcon from '../components/CategoryIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

/**
 * Convert a brand name into the same URL-safe slug the backend produces
 * (lowercase, kebab-case, no apostrophes/accents). Mirrors _brand_to_slug
 * in /app/backend/server.py so the highlight comparison stays in sync.
 */
const brandToSlug = (name) =>
  (name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .split('')
    .map((ch) => (/[a-z0-9]/.test(ch) ? ch : /[\s\-_]/.test(ch) ? '-' : ''))
    .join('')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Brand groupings for the target audience (women 35-55).
 * Based on strategy: "broad acceptance, narrow presentation".
 *
 *  - ETALAGE: front-of-house, on-brand for 35-55, mixed price point.
 *  - LUXE:    aspirational anchor, shown as an isolated section so the
 *             price gap doesn't scare budget-conscious visitors away
 *             from the rest of the grid.
 *  Everything else falls through to the "andere merken" section below.
 *
 *  Match on the lowercased brand name so small variants (e.g.
 *  "Noella Fashion" vs "Noella") still resolve.
 */
const ETALAGE_BRANDS = [
  'polin et moi',
  'robell',
  'martvisser',
  'mart visser',
  'needs no label',
  'next',
  'ivalo',
  'brahmaki',
  'allsaints',
  'freewear',
];

const LUXE_BRANDS = [
  'talbot runhof',
  'isabel marant',
  'stella mccartney',
  'roksanda',
];

const brandMatches = (brand, list) => {
  const b = (brand || '').toLowerCase().trim();
  return list.some((needle) => b === needle || b.startsWith(needle + ' '));
};

/**
 * Pick the right localised field on a record (e.g. product or category).
 * Falls back to the EN field when the FR field is missing.
 *   pickL10n({ name_nl, name_en, name_fr }, 'name', 'fr')  -> name_fr || name_en
 */
const pickL10n = (obj, baseField, lang) => {
  if (!obj) return '';
  if (lang === 'nl') return obj[`${baseField}_nl`] || obj[`${baseField}_en`] || '';
  if (lang === 'fr') return obj[`${baseField}_fr`] || obj[`${baseField}_en`] || '';
  return obj[`${baseField}_en`] || '';
};

const CategoryPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { language, t } = useLanguage();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // ?highlight=<brand-slug> – set when a visitor arrives via /shop/<brand>.
  // We pulse the matching product card briefly so it's easy to spot.
  const highlightSlug = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('highlight') || null;
  }, [location.search]);

  const fetchCategoryData = useCallback(async () => {
    setLoading(true);
    try {
      const categoryRes = await axios.get(`${API}/categories/${slug}`);
      const categoryData = categoryRes.data;
      setCategory(categoryData);

      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products?category_id=${categoryData.id}`),
        axios.get(`${API}/categories`),
      ]);
      setProducts(productsRes.data);
      setAllCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to load category data:', error);
      setCategory(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchCategoryData();
  }, [fetchCategoryData]);

  const seoMeta = categorySEO[slug] || null;

  // Build JSON-LD structured data (BreadcrumbList + ItemList + CollectionPage)
  const jsonLd = useMemo(() => {
    if (!category) return null;
    const categoryName = pickL10n(category, 'name', language);
    const pageUrl = `${SITE_URL}/category/${slug}`;

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'U&Fashion', item: SITE_URL + '/' },
        { '@type': 'ListItem', position: 2, name: categoryName, item: pageUrl },
      ],
    };

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: categoryName,
      url: pageUrl,
      numberOfItems: products.length,
      itemListElement: products.slice(0, 30).map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Product',
          name: pickL10n(p, 'name', language),
          image: p.image_url,
          brand: { '@type': 'Brand', name: p.brand },
          description: pickL10n(p, 'description', language),
          offers: {
            '@type': 'Offer',
            priceCurrency: 'EUR',
            price: p.price,
            url: p.affiliate_link,
            availability: 'https://schema.org/InStock',
          },
        },
      })),
    };

    return [breadcrumb, itemList];
  }, [category, products, slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('Categorie niet gevonden', 'Category not found', 'Catégorie introuvable')}
          </h2>
        </div>
      </div>
    );
  }

  const categoryName = pickL10n(category, 'name', language);
  const categoryDescription = pickL10n(category, 'description', language);

  // SEO content (fallback to category.description when slug not in map)
  const h1 = seoMeta
    ? (seoMeta[`h1_${language}`] || seoMeta.h1_en)
    : categoryName;
  const pageTitle = seoMeta
    ? (seoMeta[`title_${language}`] || seoMeta.title_en)
    : `${categoryName} - U&Fashion`;
  const metaDesc = seoMeta
    ? (seoMeta[`meta_${language}`] || seoMeta.meta_en)
    : categoryDescription;
  const intro = seoMeta ? (seoMeta[`intro_${language}`] || seoMeta.intro_en) : null;
  const sections = seoMeta ? seoMeta.sections : [];

  // Related categories (4 random others)
  const related = allCategories.filter((c) => c.slug !== slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="category-page">
      <SEO
        title={pageTitle}
        description={metaDesc}
        keywords={seoMeta ? seoMeta.keywords : undefined}
        ogTitle={pageTitle}
        ogDescription={metaDesc}
        canonical={`${SITE_URL}/category/${slug}`}
        jsonLd={jsonLd}
      />

      {/* Category Header */}
      <section className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="mb-5 flex justify-center">
            {/* White-tinted icon on the gradient hero (overrides the default pink) */}
            <CategoryIcon slug={category.slug} size={56} stroke={1.25} className="text-white" />
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            data-testid="category-title"
          >
            {h1}
          </h1>
          <p className="text-xl text-white/90">{categoryDescription}</p>
        </div>
      </section>

      {/* SEO intro + H2 sections */}
      {intro && (
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p
              className="text-gray-700 leading-relaxed text-base md:text-[17px]"
              data-testid="category-seo-intro"
            >
              {intro}
            </p>
            {sections.length > 0 && (
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                {sections.map((s) => (
                  <div key={s.h2_en}>
                    <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                      {s[`h2_${language}`] || s.h2_en}
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {s[`body_${language}`] || s.body_en}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Products Grid — split into Etalage / Luxe / Overige blocks
          so the aspirational luxury brands don't overwhelm the
          budget-conscious 35-55 target audience. */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">
                {t(
                  'Momenteel zijn er geen producten beschikbaar in deze categorie.',
                  'Currently there are no products available in this category.',
                  'Aucun produit n\u2019est disponible dans cette catégorie pour le moment.'
                )}
              </p>
            </div>
          ) : (
            (() => {
              const etalageOrder = (brand) => {
                const s = brandToSlug(brand);
                const norm = String(brand || '').toLowerCase().trim();
                const idx = ETALAGE_BRANDS.findIndex(
                  (b) => b === norm || brandToSlug(b) === s
                );
                return idx === -1 ? 999 : idx;
              };
              const etalage = products
                .filter((p) => brandMatches(p.brand, ETALAGE_BRANDS))
                .sort((a, b) => etalageOrder(a.brand) - etalageOrder(b.brand));
              const luxe = products.filter((p) => brandMatches(p.brand, LUXE_BRANDS));
              const rest = products.filter(
                (p) =>
                  !brandMatches(p.brand, ETALAGE_BRANDS) &&
                  !brandMatches(p.brand, LUXE_BRANDS)
              );

              const renderCard = (product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  categoryName={categoryName}
                  highlight={
                    !!highlightSlug && brandToSlug(product.brand) === highlightSlug
                  }
                />
              );

              return (
                <>
                  <div className="mb-8">
                    <p className="text-gray-600">
                      {t(
                        `${products.length} producten gevonden`,
                        `${products.length} products found`,
                        `${products.length} produits trouvés`
                      )}
                    </p>
                  </div>

                  {/* ETALAGE — top showcase, curated for 35-55 */}
                  {etalage.length > 0 && (
                    <div className="mb-14" data-testid="brand-block-etalage">
                      <div className="mb-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                          {t('Onze etalage', 'Our showcase', 'Notre vitrine')}
                        </h2>
                        <p className="text-sm md:text-base text-gray-600 mt-1">
                          {t(
                            'Handverkozen merken die aansluiten bij jouw stijl en levensfase.',
                            'Hand-picked brands that fit your style and stage of life.',
                            'Marques triées sur le volet, à votre style et à votre âge.'
                          )}
                        </p>
                      </div>
                      <div
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5"
                        data-testid="products-grid"
                      >
                        {etalage.map(renderCard)}
                      </div>
                    </div>
                  )}

                  {/* LUXE — isolated aspirational block with warm neutral tone */}
                  {luxe.length > 0 && (
                    <div
                      className="mb-14 rounded-2xl overflow-hidden border border-amber-100/60 bg-gradient-to-br from-stone-50 via-amber-50/40 to-stone-50 px-5 md:px-10 py-10 md:py-14 shadow-sm"
                      data-testid="brand-block-luxe"
                    >
                      <div className="mb-8 max-w-3xl">
                        <div className="inline-block text-[11px] tracking-[0.22em] uppercase text-amber-700/80 font-semibold mb-3">
                          {t('Luxe', 'Luxury', 'Luxe')}
                        </div>
                        <h2
                          className="text-2xl md:text-4xl font-serif font-semibold text-stone-900 leading-tight"
                          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                        >
                          {t(
                            'Onze luxe selectie',
                            'Our luxury selection',
                            'Notre sélection luxe'
                          )}
                        </h2>
                        <p className="mt-3 text-stone-700 text-sm md:text-base leading-relaxed">
                          {t(
                            'Tijdloze ontwerpen van gerenommeerde huizen. Voor die ene investering in je garderobe die jarenlang meegaat.',
                            'Timeless designs from renowned houses. For that one investment piece that stays with you for years.',
                            'Créations intemporelles de maisons renommées. Pour cet investissement qui traverse les années.'
                          )}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                        {luxe.map(renderCard)}
                      </div>
                    </div>
                  )}

                  {/* REST — everything else */}
                  {rest.length > 0 && (
                    <div data-testid="brand-block-rest">
                      {(etalage.length > 0 || luxe.length > 0) && (
                        <div className="mb-6">
                          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                            {t('Meer om te ontdekken', 'More to discover', 'À découvrir aussi')}
                          </h2>
                          <p className="text-sm md:text-base text-gray-600 mt-1">
                            {t(
                              'Extra merken en stijlen die het bekijken waard zijn.',
                              'Additional brands and styles worth exploring.',
                              'D\u2019autres marques et styles à explorer.'
                            )}
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                        {rest.map(renderCard)}
                      </div>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
      </section>

      {/* Related categories (internal linking) */}
      {related.length > 0 && (
        <section className="bg-white border-t border-gray-100" data-testid="related-categories">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('Bekijk ook deze collecties', 'Also browse these collections', 'Découvrez aussi ces collections')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {related.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className="group bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-4 text-center hover:shadow-lg transition-all duration-300"
                  data-testid={`related-category-${c.slug}`}
                >
                  <div className="mb-2 flex justify-center">
                    <CategoryIcon slug={c.slug} size={28} />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-pink-500">
                    {pickL10n(c, 'name', language)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CategoryPage;
